/**
 * =============================================================================
 * SPEQ55 RFP Pro - Usage API Functions
 * =============================================================================
 *
 * API functions for usage tracking and limit management.
 *
 * @module api/usageApi
 * @author SPEQ55
 */

import { get, put, del } from './client';

/**
 * Gets current user's usage statistics.
 *
 * @returns {Promise<Object>} Usage stats
 */
export async function getMyUsage() {
  const response = await get('/usage/my-usage');
  return response.data.usage;
}

/**
 * Gets specific user's usage statistics (admin).
 *
 * @param {string} userId - User UUID
 * @returns {Promise<Object>} Usage stats
 */
export async function getUserUsage(userId) {
  const response = await get(`/usage/users/${userId}`);
  return response.data.usage;
}

/**
 * Gets all users' usage statistics (admin).
 *
 * @param {Object} [filters] - Optional filters
 * @param {string} [filters.organizationId] - Filter by organization
 * @param {boolean} [filters.overLimitOnly] - Show only users over limit
 * @param {number} [filters.limit] - Max results
 * @param {number} [filters.offset] - Pagination offset
 * @returns {Promise<{users: Array, total: number}>} Users with usage stats
 */
export async function getAllUserUsage(filters = {}) {
  const params = new URLSearchParams();

  if (filters.organizationId) params.append('organizationId', filters.organizationId);
  if (filters.overLimitOnly) params.append('overLimitOnly', 'true');
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.offset) params.append('offset', filters.offset.toString());

  const queryString = params.toString();
  const url = queryString ? `/usage/users?${queryString}` : '/usage/users';

  const response = await get(url);
  return {
    users: response.data.users,
    total: response.data.total,
  };
}

/**
 * Sets custom usage limit for a user (admin).
 *
 * @param {string} userId - User UUID
 * @param {Object} data - Limit data
 * @param {number} data.monthlyAnalysisLimit - Custom monthly limit
 * @param {boolean} [data.limitEnabled] - Whether to enforce limit
 * @param {string} data.overrideReason - Reason for override
 * @returns {Promise<Object>} Updated limit record
 */
export async function setUserLimit(userId, data) {
  const response = await put(`/usage/users/${userId}/limit`, data);
  return response.data.limit;
}

/**
 * Removes custom usage limit for a user (admin).
 *
 * @param {string} userId - User UUID
 * @returns {Promise<Object>} Success message
 */
export async function removeUserLimit(userId) {
  const response = await del(`/usage/users/${userId}/limit`);
  return response.data;
}

export default {
  getMyUsage,
  getUserUsage,
  getAllUserUsage,
  setUserLimit,
  removeUserLimit,
};
